import { type FunctionComponent, useState } from 'react';
import { Route, Routes } from 'react-router';

import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';
import { AdminPage, ArtistMediaManager } from 'components/admin';
import { ProfileMenu } from 'components/admin/ProfileMenu';
import AdminUserProfileData from 'components/admin/UserProfileData';

import useUserArtist from 'hooks/useUserArtist';

import { toggleArtistStatus, updateArtist } from 'services/artists';
import { setAvatar, updateUser } from 'services/user';

import {
  type ArtistSelfUpdateT,
  E_INVOICING,
  E_MEDIA,
  E_MODALITIES,
  P_ADMIN_PROFILE,
  P_WEBSITE_PRACTITIONERS,
  type UserT,
  getConfig,
  getId,
  useGlobalState,
  useGlobalStateObject,
} from 'utils';

import { Invoicing } from './Invoicing';
import Modalities from './Modalities';

import S from './style.scss';

const CmsProfilePage: FunctionComponent = () => {
  const [user, setUser] = useGlobalState('user');
  if (!user) throw Error('Unauthorised');

  const [busy, setBusy] = useState(false);

  const artist = useUserArtist();

  const gs = useGlobalStateObject();
  const config = getConfig(true);

  const { data } = artist;
  const artistId = data?.id;
  const publicUrl = artistId
    ? `${config.HTTP_SCHEMA}${config.DOMAIN}${P_WEBSITE_PRACTITIONERS}/${artistId}`
    : undefined;

  return (
    <AdminPage
      contentClassName={S.content}
      subTitle="Manage your account"
      title="My Profile"
    >
      {artist.loading ? <Throbber withDebounce withOverlay /> : (
        <>
          <ProfileMenu
            baseUrl={P_ADMIN_PROFILE}
            isLive={artist.data?.published === true}
            onToggleLive={artistId ? () => {
              void (async () => {
                try {
                  setBusy(true);
                  const res = await toggleArtistStatus(artistId);
                  await artist.reload(() => res);
                } catch (error) {
                  captureException(error);
                  // eslint-disable-next-line no-alert
                  alert('Failed to toggle status');
                } finally {
                  setBusy(false);
                }
              })();
            } : undefined}
            publicUrl={publicUrl}
          />
          <Routes>
            <Route
              element={(
                <AdminUserProfileData
                  editable
                  key={artistId}
                  practitioner={artist.data}
                  updateUser={async (update) => {
                    try {
                      const userId = getId(user);
                      if (update.newAvatar) {
                        await setAvatar(userId, update.newAvatar);
                      }
                      if (update.user) {
                        const res = await updateUser(userId, update.user);
                        setUser(res);

                        let au = gs.get('adminUsers.data');
                        if (au) {
                          const idx = au.findIndex(
                            (item) => item.id === userId,
                          );
                          if (idx >= 0) {
                            au = [...au];
                            au[idx] = res;
                            gs.set('adminUsers.data', au);
                          }
                        }
                      }
                      if (update.practitioner) {
                        const {
                          user: practitionerUserUpdate,
                          ...practitionerUpdate
                        } = update.practitioner as ArtistSelfUpdateT & {
                          user?: Partial<UserT>;
                        };
                        if (practitionerUserUpdate) {
                          const res = await updateUser(
                            userId,
                            practitionerUserUpdate,
                          );
                          setUser(res);

                          let au = gs.get('adminUsers.data');
                          if (au) {
                            const idx = au.findIndex(
                              (item) => item.id === userId,
                            );
                            if (idx >= 0) {
                              au = [...au];
                              au[idx] = res;
                              gs.set('adminUsers.data', au);
                            }
                          }
                        }
                        if (
                          Object.keys(practitionerUpdate).length > 0
                          && artistId
                        ) {
                          const res = await updateArtist(
                            artistId,
                            practitionerUpdate,
                          );
                          await artist.reload(() => res);
                        }
                      }
                    } catch (error) {
                      captureException(error);
                      // eslint-disable-next-line no-alert
                      alert('Failed to save the changes');
                    }
                  }}
                  user={user}
                  withAvatarAndIntroUpload
                />
              )}
              path=""
            />
            <Route
              element={(
                <Modalities
                  key={artistId}
                  onUpdate={async (update) => {
                    try {
                      const userId = getId(user);
                      if (update.newAvatar) {
                        await setAvatar(userId, update.newAvatar);
                        const updatedUser: UserT = {
                          ...user,
                          avatarUrl: URL.createObjectURL(update.newAvatar),
                        };
                        setUser(updatedUser);
                      }
                      if (update.practitioner) {
                        const {
                          user: practitionerUserUpdate,
                          ...practitionerUpdate
                        } = update.practitioner as ArtistSelfUpdateT & {
                          user?: Partial<UserT>;
                        };
                        if (practitionerUserUpdate) {
                          const res = await updateUser(
                            userId,
                            practitionerUserUpdate,
                          );
                          setUser(res);

                          let au = gs.get('adminUsers.data');
                          if (au) {
                            const idx = au.findIndex(
                              (item) => item.id === userId,
                            );
                            if (idx >= 0) {
                              au = [...au];
                              au[idx] = res;
                              gs.set('adminUsers.data', au);
                            }
                          }
                        }
                        if (
                          Object.keys(practitionerUpdate).length > 0
                          && artistId
                        ) {
                          const res = await updateArtist(
                            artistId,
                            practitionerUpdate,
                          );
                          await artist.reload(() => res);
                        }
                      }
                    } catch (error) {
                      captureException(error);
                      // eslint-disable-next-line no-alert
                      alert('Failed to save the changes');
                    }
                  }}
                  practitioner={data ?? undefined}
                  user={user}
                />
              )}
              path={E_MODALITIES}
            />
            {artist.data ? (
              <Route
                element={(
                  <ArtistMediaManager artist={artist.data} key={artistId} />
                )}
                path={E_MEDIA}
              />
            ) : null}
            <Route
              element={(
                <Invoicing
                  editable
                  onUpdate={async (update) => {
                    try {
                      const userId = getId(user);
                      const res = await updateUser(userId, update);
                      setUser(res);

                      let au = gs.get('adminUsers.data');
                      if (au) {
                        const idx = au.findIndex(
                          (item) => item.id === userId,
                        );
                        if (idx >= 0) {
                          au = [...au];
                          au[idx] = res;
                          gs.set('adminUsers.data', au);
                        }
                      }
                    } catch (error) {
                      captureException(error);
                      // eslint-disable-next-line no-alert
                      alert('Failed to save the changes');
                    }
                  }}
                  paymentMethod={user.paymentMethod}
                />
              )}
              path={E_INVOICING}
            />
          </Routes>
        </>
      )}
      {busy ? <Throbber withDebounce withOverlay /> : null}
    </AdminPage>
  );
};

export default CmsProfilePage;
